import { getStaticCdnUrl } from '@/utils';
import { checkTodayInRange } from '@/utils/checkTodayInRange';

// --- 월별 날짜 데이터 (매달 변경되는 부분만 관리) ---

type MonthlyDates = {
  range: readonly [string, string];
  variant: 'standard' | 'donation';
  issueText: string;
  useText: string;
  step2Text: string;
  step2SubText?: string;
  step2BenefitItems?: readonly string[];
  deadline: string;
  benefitDescription: string;
  complianceText: string;
};

export const MONTHLY_DATES: MonthlyDates[] = [
  {
    range: ['2026-03-01', '2026-03-31'],
    variant: 'standard',
    issueText: '2026. 3. 1(일) ~ 2026. 3. 31(화)',
    useText: '2026. 3. 1(일) ~ 2026. 4. 10(금)',
    step2Text:
      '26년 3월 1일부터 26년 4월 10일까지\n3만원 이상 이용 시 3만원 캐시백 지급',
    deadline: '2026. 4. 30(목) 이내',
    benefitDescription: '최근 KB국민 체크카드 출금 계좌로 3만원 지급',
    complianceText:
      '준법감시인 심의필 260306-00602-HPM (2026.03.06~2027.03.05)',
  },
  {
    range: ['2026-04-01', '2026-04-30'],
    variant: 'standard',
    issueText: '2026. 4. 1(수) ~ 2026. 4. 30(목)',
    useText: '2026. 4. 1(수) ~ 2026. 5. 10(일)',
    step2Text:
      '26년 4월 1일부터 26년 5월 10일까지\n3만원 이상 이용 시 3만원 캐시백 지급',
    deadline: '2026. 5. 29(금) 이내',
    benefitDescription: '최근 KB국민 체크카드 출금 계좌로 3만원 지급',
    complianceText:
      '준법감시인 심의필 260306-00602-HPM (2026.03.06~2027.03.05)',
  },
  {
    range: ['2026-05-01', '2026-05-31'],
    variant: 'donation',
    issueText: '2026. 5. 1(금) ~ 2026. 5. 31(일)',
    useText: '2026. 5. 1(금) ~ 2026. 6. 10(수)',
    step2Text: '26년 5월 1일부터 26년 6월 10일까지',
    step2SubText:
      '신규 발급 후 해당 카드로\n고향사랑 10만원 기부하면 총 18만원 혜택',
    step2BenefitItems: [
      '10만원 세액 공제',
      '3만원 답례품',
      'KB 국민카드에서 드리는 추가 캐시백 5만원',
    ],
    deadline: '2026. 6. 30(화) 이내',
    benefitDescription: '최근 이용 KB국민체크카드 출금 계좌로 5만원 지급',
    complianceText: '준법감시인 심의필 260424-01402-HPM',
  },
];

const currentDates =
  MONTHLY_DATES.find((d) => checkTodayInRange(d.range[0], d.range[1])) ??
  MONTHLY_DATES.at(-1)!;

const KB_CARD_LINKS = {
  nori2: {
    pc: 'https://card.kbcard.com/CRD/DVIEW/HCAMCXPRICAC0076?mainCC=a&cooperationcode=07964&solicitorcode=7030160001',
    mobile:
      'https://m.kbcard.com/CRD/DVIEW/MCAMCXHIACRC0002?mainCC=a&cooperationcode=07964&solicitorcode=7030160001',
  },
  travelers: {
    pc: 'https://card.kbcard.com/CRD/DVIEW/HCAMCXPRICAC0076?mainCC=a&cooperationcode=09564&solicitorcode=7030160001',
    mobile:
      'https://m.kbcard.com/CRD/DVIEW/MCAMCXHIACRC0002?mainCC=a&cooperationcode=09564&solicitorcode=7030160001',
  },
  youthClub: {
    pc: 'https://card.kbcard.com/CRD/DVIEW/HCAMCXPRICAC0076?mainCC=a&cooperationcode=04124&solicitorcode=7030160001',
    mobile:
      'https://m.kbcard.com/CRD/DVIEW/MCAMCXHIACRC0002?mainCC=a&cooperationcode=04124&solicitorcode=7030160001',
  },
} as const;

export const KB_EVENT = {
  variant: currentDates.variant,
  /** 01 이벤트 기간 */
  period: {
    issueText: currentDates.issueText,
    useText: currentDates.useText,
  },

  /** 02 참여 대상 */
  eligibility: {
    conditions: [
      'KB국민 개인체크카드 최초 신규 발급 회원',
      '보유 체크카드 전체의 유효기간 만료 후 신규 발급 회원',
      `2025년 12월 31일(수) 이전 체크카드 탈회 후\n이벤트 기간 중 신규 발급 회원`,
    ],
  },

  /** 03 이벤트 내용 */
  content: {
    mainImage: getStaticCdnUrl('kb-card-event/main.png'),
    step2Text: currentDates.step2Text,
    step2SubText: currentDates.step2SubText,
    step2BenefitItems: currentDates.step2BenefitItems,
  },

  /** 04 대상 카드 */
  cards: [
    {
      name: '노리2 체크카드(KB Pay)',
      subtitle: '국민 대표 체크카드 시즌2 개막',
      images: [
        {
          src: getStaticCdnUrl('kb-card-event/card-1.png'),
          alt: '노리2 체크카드',
        },
      ],
      tags: ['#국내전용', '#국내외겸용(Master,VISA)', '#연회비 없음'],
      benefits: [
        { label: '커피, 모바일, 문화', highlight: '10% 할인' },
        { label: '뷰티, 편의점', highlight: '5% 할인' },
        { label: 'KB Pay', highlight: '2%추가 할인' },
      ],
      monthlyUsageNote: '※전월 이용실적 30만원 이상',
      href: KB_CARD_LINKS.nori2,
    },
    {
      name: '트래블러스 체크카드(토심이)',
      subtitle: '여행이 일상인 프로여행러라면',
      images: [
        {
          src: getStaticCdnUrl('kb-card-event/card-2.png'),
          alt: '트래블러스 체크카드',
        },
      ],
      tags: ['#국내외겸용(Master)', '#연회비 없음'],
      benefits: [
        { label: '해외 이용 수수료', highlight: '1.25% 면제' },
        { label: '철도', highlight: '5,000원 할인' },
        { label: '전국맛집', highlight: '5,000원 할인' },
      ],
      monthlyUsageNote: '※전월 이용실적 20만원 이상',
      href: KB_CARD_LINKS.travelers,
    },
    {
      name: 'Youth Club 체크카드',
      subtitle: '빛나는 혜택, 청춘의 특권',
      images: [
        {
          src: getStaticCdnUrl('kb-card-event/card-3.png'),
          alt: 'Youth Club 체크카드 1',
        },
        {
          src: getStaticCdnUrl('kb-card-event/card-4.png'),
          alt: 'Youth Club 체크카드 2',
        },
      ],
      tags: ['#국내전용', '#국내외겸용(Master)', '#연회비 없음'],
      benefits: [
        {
          label: '[A팩] 6개 영역(OTT, APP등)',
          highlight: '최대 50% 할인',
        },
        {
          label: '[B팩] 6개 영역(쇼핑멤버십, 통신 등)',
          highlight: '최대 50% 할인',
        },
        { label: 'Mastercard', highlight: '플래티늄 기본서비스 제공' },
      ],
      monthlyUsageNote: '※전월 이용실적 20만원 이상',
      href: KB_CARD_LINKS.youthClub,
    },
  ],

  /** 05 혜택 제공 일정 */
  benefit: {
    deadline: currentDates.deadline,
    description: currentDates.benefitDescription,
  },

  /** 06 유의사항 */
  notices: {
    general: [
      '채널 內 이벤트 배너 통한 발급 필수(자동 응모 적용)',
      '기존 체크카드 회원의 추가/교체 발급, 유효기간 미경과 해지 회원의 재발급 시 대상 제외',
      '이용금액은 이용기간 종료 후 3영업일까지 정상 매입된 금액에 한하며, 매출취소 및 KB국민카드 포인트리 충전금액 제외',
      '혜택 지급 전 해지, 거래정지, 연체 시 대상에서 제외',
      '연회비 : 없음',
      '계약을 체결하기 전에 상품설명서와 약관을 확인하시기 바랍니다.',
    ],
    legal: [
      '연체이자율은 "회원별, 이용상품별 약정금리 + 최대 연3%p, 법정 최고금리(연20%)이내"에서 적용됩니다.',
      '단, 연체 발생 시점에 약정금리가 없는 경우 아래와 같이 적용함.',
      '그 외의 경우: 정상이자율은 상법상 상사법정이율과 상호금융가계자금대출금리*중 높은 금리 적용 *한국은행에서 매월 발표하는 가장 최근의 비은행 금융기관 가중평균대출금리(신규대출 기준)',
      '상환능력에 비해 신용카드 사용액이 과도할 경우, 귀하의 개인신용평점이 하락할 수 있습니다.',
      '개인신용평점 하락시 금융거래와 관련된 불이익이 발생할 수 있습니다.',
      '일정기간 신용카드 이용대금을 연체할 경우, 결제일이 도래하지 않은 모든 신용카드 이용대금을 변제할 의무가 발생할 수 있습니다.',
    ],
    legalSub: [
      '일시불 거래 연체 시 : 거래 발생 시점의 최소기간(2개월) 유이자할부 금리',
      '무이자 할부 거래 연체 시 : 거래 발생 시점의 동일한 할부 계약기간의 유이자 할부 금리',
    ],
    youthClubPromoLink: 'https://m.kbcard.com/z/e1000416',
    complianceText: currentDates.complianceText,
  },
} as const;
